import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Users, Briefcase, Heart, ShoppingBag, GraduationCap, HandCoins, Check, X as XIcon, CircleNotch, Trash, Stethoscope, Scales, Confetti, Ticket, Bell, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const MENUS = ["matrimony", "jobs", "marketplace", "doctor", "training", "donations", "lawyer", "events"];
const TABS = ["Overview", "Moderation", "Managers", "Donations", "Partners", "Sellers"];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [pending, setPending] = useState({});
  const [managers, setManagers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [totals, setTotals] = useState({});
  const [busy, setBusy] = useState(null);

  const isAdmin = user && user !== false && user.role === "admin";
  const isManager = user && user !== false && user.role === "manager";

  const loadOverview = async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (err) { console.error("load stats failed", err); }
    try { const { data } = await api.get("/admin/recent-activity"); setActivity(data); } catch (err) { console.error("load activity failed", err); }
  };
  const loadPending = async () => {
    try { const { data } = await api.get("/admin/pending"); setPending(data); } catch (err) { console.error("load pending failed", err); }
    try { const { data } = await api.get("/admin/pending-extras"); setPending((prev) => ({ ...prev, ...data })); } catch (err) { console.error("load pending extras failed", err); }
  };
  const loadManagers = async () => {
    try { const { data } = await api.get("/admin/managers"); setManagers(data); } catch (err) { console.error("load managers failed", err); }
  };
  const loadDonations = async () => {
    try {
      const [{ data: rows }, { data: t }] = await Promise.all([
        api.get("/donations"),
        api.get("/admin/donations/totals"),
      ]);
      setDonations(rows); setTotals(t);
    } catch (err) { console.error("load donations failed", err); }
  };

  useEffect(() => {
    if (tab === "Overview") loadOverview();
    if (tab === "Moderation") loadPending();
    if (tab === "Managers") loadManagers();
    if (tab === "Donations") loadDonations();
    // eslint-disable-next-line
  }, [tab, user]);

  if (loading) return <div className="p-12 text-[#5C564D]">Loading…</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (!isAdmin && !isManager) {
    return <div className="max-w-lg mx-auto px-6 py-24 text-center">
      <ShieldCheck size={40} className="mx-auto text-[#D95A41]" />
      <h1 className="font-heading text-3xl mt-3">Admins & Managers only</h1>
      <p className="text-[#5C564D] mt-2">Ask the community admin to grant you a role.</p>
      <Link to="/" className="samaaj-btn-primary mt-6 inline-block">Back home</Link>
    </div>;
  }

  const moderate = async (collection, id, action, reason = "") => {
    setBusy(`${collection}-${id}-${action}`);
    try {
      await api.post("/admin/moderate", { collection, id, action, reason });
      toast.success(action === "approve" ? "Approved & live" : "Rejected");
      loadPending();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const availableTabs = isAdmin ? TABS : ["Moderation"];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-root">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">Anveshan control room</div>
      <h1 className="font-heading text-5xl mt-2 tracking-tight">Admin panel</h1>
      <p className="text-[#5C564D] mt-2">Fair delivery, honest service — every submission gets a pair of eyes.</p>

      <div className="mt-6 inline-flex p-1 rounded-full bg-[#F2EBE5] border border-[#E5D9CC]" data-testid="admin-tabs">
        {availableTabs.map((tt) => (
          <button
            key={tt} data-testid={`tab-${tt.toLowerCase()}`}
            onClick={() => setTab(tt)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold"
            style={{ background: tab === tt ? "#D95A41" : "transparent", color: tab === tt ? "#F9F6F0" : "#5C564D",
              transition: "background-color 180ms ease-out, color 180ms ease-out" }}
          >{tt}</button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <div className="mt-8 space-y-8" data-testid="admin-stats">
          {/* Headline banner */}
          <div className="samaaj-card p-6 bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#F9F6F0]/70">Live community pulse</div>
                <div className="font-heading text-4xl mt-1">{stats.users} members</div>
                <div className="text-xs text-[#F9F6F0]/80 mt-1">+{stats.signups_24h} in the last 24h · +{stats.signups_7d} this week</div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><div className="font-heading text-2xl">{stats.orders_paid}</div><div className="text-[10px] text-[#F9F6F0]/70 uppercase">orders paid</div></div>
                <div><div className="font-heading text-2xl">{stats.event_checked_in}</div><div className="text-[10px] text-[#F9F6F0]/70 uppercase">checked in</div></div>
                <div><div className="font-heading text-2xl">{stats.coupons_redeemed}</div><div className="text-[10px] text-[#F9F6F0]/70 uppercase">coupons used</div></div>
              </div>
            </div>
          </div>

          {/* Users by role */}
          <div>
            <SectionTitle icon={Users}>Members by role</SectionTitle>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {Object.entries(stats.users_by_type || {}).map(([k, v]) => (
                <div key={k} className="samaaj-card p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C564D]">{k}</div>
                  <div className="font-heading text-2xl">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Core module stats */}
          <div>
            <SectionTitle icon={ChartLineUp}>Module activity</SectionTitle>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <Stat icon={Heart} label="Matrimony profiles" value={stats.matrimony_profiles} />
              <Stat icon={Briefcase} label="Jobs posted" value={stats.jobs} tone="green" />
              <Stat icon={Briefcase} label="Applications" value={stats.job_applications} tone="green" />
              <Stat icon={ShoppingBag} label="Listings" value={stats.listings} tone="ochre" />
              <Stat icon={ShoppingBag} label="Sold" value={stats.listings_sold} tone="ochre" />
              <Stat icon={ShoppingBag} label="COD in transit" value={stats.orders_cod_pending} tone="ochre" />
              <Stat icon={GraduationCap} label="Enrollments" value={stats.enrollments} />
              <Stat icon={GraduationCap} label="Certificates" value={stats.certificates_issued} />
              <Stat icon={Stethoscope} label="Doctors listed" value={stats.doctor_listings} />
              <Stat icon={Stethoscope} label="Consults" value={stats.consultations} />
              <Stat icon={Stethoscope} label="Appointments" value={stats.appointments_confirmed} />
              <Stat icon={Scales} label="Lawyers listed" value={stats.lawyer_listings} />
              <Stat icon={Scales} label="Legal queries" value={stats.legal_consultations} />
              <Stat icon={Confetti} label="Events" value={stats.events_approved} />
              <Stat icon={Confetti} label="RSVPs" value={stats.event_rsvps} />
              <Stat icon={Ticket} label="Coupons active" value={stats.coupons_active} tone="ochre" />
              <Stat icon={HandCoins} label="Donations" value={stats.donations} tone="ochre" />
              <Stat icon={HandCoins} label="Support tickets" value={stats.support_requests} tone="ochre" />
              <Stat icon={Bell} label="Push subscribers" value={stats.push_subscriptions} />
              <Stat icon={Stethoscope} label="AI doctor chats" value={stats.doctor_chats} />
            </div>
          </div>

          {/* Pending review */}
          <div>
            <SectionTitle icon={ShieldCheck}>Pending moderator review</SectionTitle>
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {Object.entries(stats.pending || {}).map(([k, v]) => (
                <button key={k} onClick={() => setTab("Moderation")} data-testid={`pending-${k}`} className={`samaaj-card p-3 text-center transition-colors ${v > 0 ? "border-[#D49A36] bg-[#D49A36]/10" : ""}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#5C564D]">{k}</div>
                  <div className={`font-heading text-2xl ${v > 0 ? "text-[#7a5a10]" : ""}`}>{v}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Recent activity */}
          {activity && (
            <div>
              <SectionTitle icon={ChartLineUp}>Recent activity</SectionTitle>
              <div className="mt-3 grid lg:grid-cols-2 gap-4">
                <ActivityBlock title="Newest members" items={activity.users} render={(u) => (<><span className="font-semibold">{u.name}</span> <span className="text-[#5C564D] text-xs">· {u.user_type || "user"} · {u.email}</span></>)} time={(u) => u.created_at} />
                <ActivityBlock title="Latest orders" items={activity.orders} render={(o) => (<><span className="font-semibold">{o.listing_title}</span> <span className="text-[#5C564D] text-xs">· ${o.amount} · {o.provider} · {o.payment_status}</span></>)} time={(o) => o.created_at} />
                <ActivityBlock title="Doctor consultations" items={activity.consultations} render={(c) => (<><span className="font-semibold">{c.title}</span> <span className="text-[#5C564D] text-xs">· {c.body_system} · {c.patient_name}</span></>)} time={(c) => c.created_at} />
                <ActivityBlock title="Legal consultations" items={activity.legal_consultations} render={(c) => (<><span className="font-semibold">{c.title}</span> <span className="text-[#5C564D] text-xs">· {c.category} · {c.client_name}</span></>)} time={(c) => c.created_at} />
                <ActivityBlock title="Latest events" items={activity.events} render={(e) => (<><span className="font-semibold">{e.title}</span> <span className="text-[#5C564D] text-xs">· {e.venue} · {e.approval_status}</span></>)} time={(e) => e.created_at} />
                <ActivityBlock title="Coupons" items={activity.coupons} render={(c) => (<><span className="font-mono">{c.code}</span> <span className="text-[#5C564D] text-xs">· ${c.amount_usd} · {c.redeemed ? "used" : "active"} · {c.reason || ""}</span></>)} time={(c) => c.issued_at} />
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "Moderation" && (
        <div className="mt-8 space-y-8" data-testid="admin-moderation">
          <PendingBlock title="Jobs awaiting review" collection="jobs" items={pending.jobs || []} render={(j) => (<><div className="font-semibold">{j.title} · {j.company}</div><div className="text-xs text-[#5C564D] mt-0.5">{j.location} · {j.experience} · Requires: {j.qualification_required}</div><p className="text-xs text-[#5C564D] mt-2 line-clamp-2">{j.description}</p></>)} moderate={moderate} busy={busy} />
          <PendingBlock title="Matrimony profiles awaiting review" collection="matrimony_profiles" items={pending.matrimony || []} render={(p) => (<><div className="font-semibold">{p.full_name}, {p.age} · {p.community}{p.gotra ? ` · ${p.gotra}` : ""}</div><div className="text-xs text-[#5C564D] mt-0.5">{p.profession} · {p.location}</div><p className="text-xs text-[#5C564D] mt-2 line-clamp-2">{p.about}</p></>)} moderate={moderate} busy={busy} />
          <PendingBlock title="Marketplace listings awaiting review" collection="listings" items={pending.marketplace || []} render={(l) => (<><div className="font-semibold">{l.title} · ${l.price}</div><div className="text-xs text-[#5C564D] mt-0.5">{l.category} · {l.condition_type} · {l.location}</div><p className="text-xs text-[#5C564D] mt-2 line-clamp-2">{l.description}</p></>)} moderate={moderate} busy={busy} />
          <PendingBlock title="Doctor listings awaiting review" collection="doctor_listings" items={pending.doctors || []} render={(d) => (<><div className="font-semibold">Dr. {d.full_name} · {d.specialization}</div><div className="text-xs text-[#5C564D] mt-0.5">{d.city} · {d.years_experience || 0} yrs · {(d.body_systems || []).join(", ")}</div><p className="text-xs text-[#5C564D] mt-2 line-clamp-2">{d.bio}</p></>)} moderate={moderate} busy={busy} />
          <PendingBlock title="Faculty videos awaiting review" collection="faculty_videos" items={pending.videos || []} render={(v) => (<><div className="font-semibold">{v.title}</div><div className="text-xs text-[#5C564D] mt-0.5">{v.faculty_name} · Week {(v.lesson_index ?? 0) + 1}</div><a href={v.video_url} target="_blank" rel="noreferrer" className="text-xs text-[#2C4A3B] underline break-all">{v.video_url}</a></>)} moderate={moderate} busy={busy} />
          <PendingBlock title="Lawyer listings awaiting review" collection="lawyer_listings" items={pending.lawyers || []} render={(d) => (<><div className="font-semibold">Adv. {d.full_name} · {d.specialization}</div><div className="text-xs text-[#5C564D] mt-0.5">{d.city} · {d.years_experience || 0} yrs · {(d.categories || []).join(", ")}</div><p className="text-xs text-[#5C564D] mt-2 line-clamp-2">{d.bio}</p></>)} moderate={moderate} busy={busy} />
          <PendingBlock title="Events awaiting review" collection="events" items={pending.events || []} render={(e) => (<><div className="font-semibold">{e.title}</div><div className="text-xs text-[#5C564D] mt-0.5">{e.event_date ? new Date(e.event_date).toLocaleString() : ""} · {e.venue}</div><p className="text-xs text-[#5C564D] mt-2 line-clamp-2">{e.description}</p></>)} moderate={moderate} busy={busy} />
          {["jobs", "matrimony", "marketplace", "doctors", "videos", "lawyers", "events"].every((k) => !(pending[k] && pending[k].length)) && (
            <div className="samaaj-card p-10 text-center text-[#5C564D]">All caught up — no pending items right now.</div>
          )}
        </div>
      )}

      {tab === "Managers" && isAdmin && (
        <ManagersTab managers={managers} onChange={loadManagers} />
      )}

      {tab === "Donations" && isAdmin && (
        <DonationsTab donations={donations} totals={totals} onChange={loadDonations} />
      )}

      {tab === "Partners" && isAdmin && (
        <PartnersTab />
      )}

      {tab === "Sellers" && isAdmin && (
        <SellersTab />
      )}
    </div>
  );
}


function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">
      <Icon size={14} weight="duotone" />
      {children}
    </div>
  );
}

function ActivityBlock({ title, items, render, time }) {
  return (
    <div className="samaaj-card p-5">
      <div className="text-xs uppercase tracking-wider font-bold text-[#2C4A3B]">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-[#5C564D] mt-3 italic">Nothing yet.</div>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((it, i) => (
            <li key={it.id || i} className="text-sm text-[#1C1B1A] flex items-center justify-between gap-2 border-b border-[#E5D9CC] last:border-b-0 pb-1">
              <div className="min-w-0 truncate">{render(it)}</div>
              <div className="text-[10px] text-[#5C564D] shrink-0">{time(it) ? new Date(time(it)).toLocaleString() : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function Stat({ icon: Icon, label, value, tone = "green" }) {
  const bg = tone === "ochre" ? "#EFE4D3" : "#DDEBDF";
  const color = tone === "ochre" ? "#D49A36" : "#2C4A3B";
  return (
    <div className="samaaj-card p-5">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: bg }}>
          <Icon size={16} weight="fill" style={{ color }} />
        </div>
        <div className="text-xs uppercase tracking-wider font-bold text-[#5C564D]">{label}</div>
      </div>
      <div className="font-heading text-4xl mt-2 text-[#1C1B1A]">{value ?? "—"}</div>
    </div>
  );
}

function PendingBlock({ title, collection, items, render, moderate, busy }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">{title} · {items.length}</div>
      <div className="mt-3 space-y-3">
        {items.map((it) => {
          const bk = `${collection}-${it.id}`;
          return (
            <div key={it.id} className="samaaj-card p-5 flex flex-col sm:flex-row gap-4 justify-between" data-testid={`pending-${collection}-${it.id}`}>
              <div className="flex-1 min-w-0">{render(it)}</div>
              <div className="flex gap-2">
                <button data-testid={`approve-${it.id}`} disabled={!!busy} onClick={() => moderate(collection, it.id, "approve")} className="samaaj-btn-primary text-xs flex items-center gap-1">
                  {busy === `${bk}-approve` ? <CircleNotch className="animate-spin" size={12} /> : <Check size={12} weight="bold" />} Approve
                </button>
                <button data-testid={`reject-${it.id}`} disabled={!!busy} onClick={() => {
                  const reason = window.prompt("Reason (shared with the submitter):", "");
                  if (reason == null) return;
                  moderate(collection, it.id, "reject", reason);
                }} className="samaaj-btn-outline text-xs flex items-center gap-1">
                  <XIcon size={12} weight="bold" /> Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ManagersTab({ managers, onChange }) {
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const menus = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!email || menus.length === 0) { toast.error("Enter email and at least one menu"); return; }
    setBusy(true);
    try {
      await api.post("/admin/managers", { email, menus, action: "assign" });
      toast.success(`Assigned as manager for ${menus.length} menu(s)`);
      setEmail(""); setSelected({}); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async (m) => {
    if (!window.confirm(`Revoke manager role from ${m.email}?`)) return;
    try {
      await api.post("/admin/managers", { email: m.email, menus: [], action: "remove" });
      toast.success("Revoked");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-8 grid lg:grid-cols-2 gap-6" data-testid="admin-managers">
      <form onSubmit={submit} className="samaaj-card p-6">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">Assign a manager</div>
        <input data-testid="mgr-email" required type="email" placeholder="manager@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-3 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
        <div className="mt-3 text-xs font-semibold text-[#5C564D]">Menus they can moderate</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {MENUS.map((m) => (
            <label key={m} data-testid={`menu-${m}`} className="flex items-center gap-2 cursor-pointer text-sm capitalize">
              <input type="checkbox" checked={!!selected[m]} onChange={(e) => setSelected({ ...selected, [m]: e.target.checked })} className="w-4 h-4 accent-[#D95A41]" />
              {m}
            </label>
          ))}
        </div>
        <button data-testid="mgr-assign" disabled={busy} className="samaaj-btn-primary w-full mt-4">{busy ? "Saving…" : "Assign"}</button>
      </form>

      <div className="samaaj-card p-6">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">Current managers · {managers.length}</div>
        {managers.length === 0 ? (
          <div className="text-sm text-[#5C564D] mt-3">No managers yet — assign a trusted community member to help review submissions.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {managers.map((m) => (
              <div key={m.id} className="p-3 rounded-xl bg-[#F2EBE5] border border-[#E5D9CC] flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[#1C1B1A]">{m.name} <span className="text-xs text-[#5C564D]">· {m.email}</span></div>
                  <div className="text-xs text-[#5C564D] mt-1 flex flex-wrap gap-1">
                    {(m.menus || []).map((mm) => (<span key={mm} className="samaaj-chip capitalize">{mm}</span>))}
                  </div>
                </div>
                <button data-testid={`revoke-${m.email}`} onClick={() => revoke(m)} className="samaaj-btn-outline text-xs flex items-center gap-1"><Trash size={12} /> Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DonationsTab({ donations, totals, onChange }) {
  const [form, setForm] = useState({ amount: 25, currency: "usd", purpose: "General fund", beneficiary: "", note: "" });
  const [busy, setBusy] = useState(false);
  const record = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/donations", { ...form, amount: Number(form.amount) });
      toast.success("Donation recorded");
      setForm({ ...form, note: "", beneficiary: "" });
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 space-y-6" data-testid="admin-donations">
      <div className="grid sm:grid-cols-3 gap-4">
        {Object.entries(totals).map(([cur, t]) => (
          <div key={cur} className="samaaj-card p-5">
            <div className="text-xs uppercase tracking-wider font-bold text-[#D49A36]">{cur.toUpperCase()} received</div>
            <div className="font-heading text-4xl mt-1">{t.total.toLocaleString()}</div>
            <div className="text-xs text-[#5C564D]">{t.count} contribution(s)</div>
          </div>
        ))}
        {Object.keys(totals).length === 0 && (
          <div className="samaaj-card p-5 col-span-3 text-sm text-[#5C564D]">No donations recorded yet.</div>
        )}
      </div>

      <form onSubmit={record} className="samaaj-card p-6 grid sm:grid-cols-2 gap-3">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B] sm:col-span-2">Record a donation</div>
        <input data-testid="don-amount" required type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
        <select data-testid="don-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
          <option value="usd">USD</option>
          <option value="inr">INR</option>
        </select>
        <input data-testid="don-purpose" placeholder="Purpose (e.g. Medical relief)" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
        <input data-testid="don-beneficiary" placeholder="Beneficiary (optional)" value={form.beneficiary} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
        <input data-testid="don-note" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="sm:col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
        <button data-testid="don-submit" disabled={busy} className="samaaj-btn-primary sm:col-span-2">{busy ? "Recording…" : "Record donation"}</button>
      </form>

      <div className="samaaj-card p-6">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#2C4A3B]">Ledger · {donations.length}</div>
        <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
          {donations.length === 0 ? <div className="text-sm text-[#5C564D]">Nothing here yet.</div> :
            donations.map((d) => (
              <div key={d.id} className="p-3 rounded-xl bg-[#F2EBE5] border border-[#E5D9CC] flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[#1C1B1A]">{d.currency.toUpperCase()} {d.amount} · {d.purpose}</div>
                  <div className="text-xs text-[#5C564D] mt-0.5">by {d.donor_name || "Anonymous"}{d.beneficiary ? ` → ${d.beneficiary}` : ""}{d.note ? ` · ${d.note}` : ""}</div>
                </div>
                <div className="text-[10px] text-[#5C564D]">{new Date(d.created_at).toLocaleDateString()}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}


function PartnersTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", logo_url: "", website: "", partner_type: "organization", description: "", display_order: 100, approved: true });
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/admin/partners").then(({ data }) => setRows(data)).catch((err) => console.error(err));
  useEffect(() => { load(); }, []);
  const reset = () => { setForm({ name: "", logo_url: "", website: "", partner_type: "organization", description: "", display_order: 100, approved: true }); setEditingId(null); };
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, display_order: Number(form.display_order) || 100 };
    try {
      if (editingId) await api.put(`/admin/partners/${editingId}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success("Saved"); reset(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const edit = (p) => { setEditingId(p.id); setForm({ ...form, ...p }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    try { await api.delete(`/admin/partners/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-5" data-testid="partners-admin">
      <form onSubmit={save} className="samaaj-card p-5" data-testid="partner-form">
        <div className="font-heading text-xl text-[#2C4A3B]">{editingId ? "Edit partner" : "Add a partner"}</div>
        <p className="text-xs text-[#5C564D] mt-1">The home-page strip under Services only appears when at least one approved partner exists.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Name</div>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white text-sm" /></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Partner type</div>
            <select value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })} data-testid="pf-type" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white text-sm">
              <option value="training_provider">Training provider</option>
              <option value="organization">Partner organisation</option>
              <option value="sponsor">Supporter / sponsor</option>
            </select></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Logo URL</div>
            <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="pf-logo" placeholder="https://…" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white text-sm" /></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Website</div>
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="pf-site" placeholder="https://…" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white text-sm" /></label>
          <label className="block text-xs"><div className="text-[#5C564D] mb-1">Display order (lower = earlier)</div>
            <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} data-testid="pf-order" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white text-sm" /></label>
          <label className="flex items-center gap-2 text-xs text-[#5C564D] mt-6">
            <input type="checkbox" checked={!!form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} data-testid="pf-approved" />
            Approved (visible on home page)
          </label>
          <label className="block text-xs sm:col-span-2"><div className="text-[#5C564D] mb-1">Short description (tooltip)</div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pf-desc" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white text-sm" /></label>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="submit" data-testid="pf-save" className="rounded-full px-5 py-2 text-sm font-semibold text-white bg-[#2C4A3B]">{editingId ? "Save changes" : "Add partner"}</button>
          {editingId && <button type="button" onClick={reset} className="rounded-full px-4 py-2 text-sm font-semibold border border-[#5C564D] text-[#5C564D]">Cancel</button>}
        </div>
      </form>
      <div className="samaaj-card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5D9CC] text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">Current partners ({rows.length})</div>
        {!rows.length ? (
          <div className="p-6 text-sm text-[#5C564D]">No partners added yet. The home-page strip is hidden until one approved partner exists.</div>
        ) : (
          <ul className="divide-y divide-[#E5D9CC]">
            {rows.map((p) => (
              <li key={p.id} className="px-5 py-3 flex items-center gap-4" data-testid={`admin-partner-${p.id}`}>
                {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-8 w-auto max-w-[90px] object-contain" /> : <div className="h-8 px-3 flex items-center text-sm font-heading text-[#2C4A3B] border border-[#E5D9CC] rounded-lg bg-white">{p.name}</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#1C1B1A] truncate">{p.name}</div>
                  <div className="text-[11px] text-[#5C564D] truncate">{p.partner_type} · order {p.display_order} · {p.approved ? "approved" : "hidden"}{p.website ? ` · ${p.website}` : ""}</div>
                </div>
                <button onClick={() => edit(p)} data-testid={`pf-edit-${p.id}`} className="text-xs rounded-full px-3 py-1 border border-[#2C4A3B] text-[#2C4A3B]">Edit</button>
                <button onClick={() => del(p.id)} data-testid={`pf-del-${p.id}`} className="text-xs rounded-full px-3 py-1 border border-[#B91C1C] text-[#B91C1C]">Delete</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SellersTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get("/admin/sellers", { params: filter ? { status: filter } : {} }).then(({ data }) => setRows(data)).catch((err) => console.error(err));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const act = async (seller_user_id, action) => {
    try { await api.post("/admin/sellers/verify", { seller_user_id, action }); toast.success(action); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="samaaj-card p-5" data-testid="sellers-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs tracking-widest uppercase font-bold text-[#2C4A3B]">Seller verification queue</div>
          <div className="font-heading text-xl">Review & verify sellers</div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {["pending", "under_review", "verified", "rejected", "suspended", ""].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`sv-${s || "all"}`} className="text-xs px-3 py-1 rounded-full border"
              style={{ background: filter === s ? "#2C4A3B" : "white", color: filter === s ? "white" : "#2C4A3B", borderColor: "#2C4A3B" }}>
              {s || "all"}
            </button>
          ))}
        </div>
      </div>
      {!rows.length ? <div className="mt-4 text-sm text-[#5C564D]">No sellers in this bucket.</div> : (
        <div className="mt-4 space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#E5D9CC] p-3 bg-white" data-testid={`sv-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-[#1C1B1A]">{r.business_name || r.legal_name || "Unnamed"}</div>
                  <div className="text-[11px] text-[#5C564D]">{r.seller_type} · {r.business_category || "—"} · {r.business_address || r.pickup_location || "—"}</div>
                  <div className="text-[11px] mt-0.5 text-[#5C564D]">
                    {r.pan_last4 && <>PAN ••••{r.pan_last4} · </>}
                    {r.gstin_last4 && <>GSTIN ••••{r.gstin_last4} · </>}
                    {r.aadhaar_alt_proof && <>Alt: {r.aadhaar_alt_proof}</>}
                  </div>
                  {r.verify_note && <div className="text-[11px] mt-0.5 italic text-[#5C564D]">Note: {r.verify_note}</div>}
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: "#F2EBE5", color: "#2C4A3B" }} data-testid={`sv-stat-${r.id}`}>
                  {r.verification_status || "pending"}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <button onClick={() => act(r.user_id, "under_review")} data-testid={`sv-review-${r.id}`} className="text-xs rounded-full px-3 py-1 border border-[#D97706] text-[#D97706]">Mark under review</button>
                <button onClick={() => act(r.user_id, "approve")} data-testid={`sv-approve-${r.id}`} className="text-xs rounded-full px-3 py-1 text-white" style={{ background: "#15803D" }}>Approve</button>
                <button onClick={() => act(r.user_id, "reject")} data-testid={`sv-reject-${r.id}`} className="text-xs rounded-full px-3 py-1 border border-[#B91C1C] text-[#B91C1C]">Reject</button>
                <button onClick={() => act(r.user_id, "suspend")} data-testid={`sv-suspend-${r.id}`} className="text-xs rounded-full px-3 py-1 border border-[#B91C1C] text-[#B91C1C]">Suspend</button>
                {r.verification_status === "suspended" && <button onClick={() => act(r.user_id, "reinstate")} data-testid={`sv-reinstate-${r.id}`} className="text-xs rounded-full px-3 py-1 border border-[#2C4A3B] text-[#2C4A3B]">Reinstate</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

